import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { FsLvtAccountSettings } from '../models/lvt-account-settings';
import { FsLvtCamera } from '../models/lvt-camera';
import { FsLvtLights } from '../models/lvt-lights';
import { FsLvtSounds } from '../models/lvt-sounds';
import { FsLvtToken } from '../models/lvt-token';

@Injectable({
  providedIn: 'root',
})
export class SnLvtService {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
  ) {}

  public getAccountSettings(): Observable<FsLvtAccountSettings[]> {
    return this._httpClient
      .get('/api/integrations/lvt/account-settings/')
      .pipe(
        map((response: any) => response?.map((item: object) => new FsLvtAccountSettings(item))),
      );
  }

  public getAccountSetting(accountName: string): Observable<FsLvtAccountSettings> {
    return this._httpClient
      .get(`/api/integrations/lvt/account-settings/${accountName}/`)
      .pipe(map((response: FsLvtAccountSettings) => new FsLvtAccountSettings(response)));
  }

  public postAccountSettings(payload: FsLvtAccountSettings): Observable<any> {
    return this._httpClient
      .post('/api/integrations/lvt/account-settings/', {
        account_settings: [payload],
      })
      .pipe(
        tap({
          next: () => this._toastrService.success('LVT credentials created'),
        }),
      );
  }

  public patchAccountSettings(accountName: string, payload: FsLvtAccountSettings): Observable<any> {
    return this._httpClient
      .patch(`/api/integrations/lvt/account-settings/${accountName}/`, {
        account_settings: [payload],
      })
      .pipe(
        tap({
          next: () => this._toastrService.success('LVT credentials updated'),
        }),
      );
  }

  public deleteAccountSettings(accountName: string): Observable<any> {
    return this._httpClient.delete(`/api/integrations/lvt/account-settings/${accountName}/`).pipe(
      tap({
        next: () => this._toastrService.success('LVT credentials deleted'),
      }),
    );
  }

  public getLvtCameras(accountName: string = 'all'): Observable<any> {
    return this._httpClient
      .get('/api/integrations/lvt/cameras/v2/', { params: { accountName } })
      .pipe(
        map((response: any) => ({ results: response?.map((item) => new FsLvtCamera(item)) })),
        catchError(() =>
          this._httpClient.get('/api/integrations/lvt/cameras/').pipe(
            // Fallback to old endpoint if new one fails (or is not there).
            map((response: any) => ({ results: response?.map((item) => new FsLvtCamera(item)) })),
            tap({
              error: () =>
                this._toastrService.error('Could not retrieve lvt cameras. Please try again later'),
            }),
          ),
        ),
      );
  }

  public getLvtCams(liveUnitId: string, accountName: string = 'all'): Observable<any> {
    return this._httpClient
      .get('/api/integrations/lvt/cameras/v2/', { params: { accountName } })
      .pipe(
        map((response: any) => {
          return response
            ?.map((item) => new FsLvtCamera(item))
            .filter((item) => item.liveUnitId === liveUnitId);
        }),
        catchError(() =>
          this._httpClient.get('/api/integrations/lvt/cameras/').pipe(
            // Fallback to old endpoint if new one fails (or is not there).
            map((response: any) => {
              return response
                ?.map((item) => new FsLvtCamera(item))
                .filter((item) => item.liveUnitId === liveUnitId);
            }),
            tap({
              error: () =>
                this._toastrService.error('Could not retrieve lvt cameras. Please try again later'),
            }),
          ),
        ),
      );
  }

  public patchLvtCamPosition(cameraId: any, accountName: string, body: any): Observable<any> {
    return this._httpClient
      .patch(`/api/integrations/lvt/cameras/${cameraId}/position/v2/`, body, {
        params: { accountName },
      })
      .pipe(
        catchError(() =>
          this._httpClient.patch(`/api/integrations/lvt/cameras/${cameraId}/position/`, body),
        ),
      );
  }

  public getLvtLights(liveUnitId: any, accountName: string): Observable<any> {
    return this._httpClient
      .get(`/api/integrations/lvt/live-units/${liveUnitId}/lights/v2/`, { params: { accountName } })
      .pipe(
        map((response: any) => response?.map((item) => new FsLvtLights(item))),
        catchError(() =>
          this._httpClient
            .get(`/api/integrations/lvt/live-units/${liveUnitId}/lights/`)
            .pipe(map((response: any) => response?.map((item) => new FsLvtLights(item)))),
        ),
      );
  }

  public postLvtLights(
    liveUnitId: any,
    accountName: string,
    lightId: any,
    body?: any,
  ): Observable<any> {
    return this._httpClient
      .post(`/api/integrations/lvt/live-units/${liveUnitId}/lights/${lightId}/toggle/v2/`, body, {
        params: { accountName },
      })
      .pipe(
        catchError(() =>
          this._httpClient.post(
            `/api/integrations/lvt/live-units/${liveUnitId}/lights/${lightId}/toggle/`,
            body,
          ),
        ),
      );
  }

  public getLvtSounds(liveUnitId: any, accountName: string): Observable<any> {
    return this._httpClient
      .get(`/api/integrations/lvt/live-units/${liveUnitId}/sounds/v2/`, { params: { accountName } })
      .pipe(
        map((response: any) => response?.map((item) => new FsLvtSounds(item))),
        catchError(() =>
          this._httpClient
            .get(`/api/integrations/lvt/live-units/${liveUnitId}/sounds/`)
            .pipe(map((response: any) => response?.map((item) => new FsLvtSounds(item)))),
        ),
      );
  }

  public postLvtSounds(liveUnitId: any, accountName: string, soundId: any): Observable<any> {
    return this._httpClient
      .post(`/api/integrations/lvt/live-units/${liveUnitId}/sounds/${soundId}/play/v2/`, null, {
        params: { accountName },
      })
      .pipe(
        catchError(() =>
          this._httpClient.post(
            `/api/integrations/lvt/live-units/${liveUnitId}/sounds/${soundId}/play/`,
            null,
          ),
        ),
      );
  }

  public getToken(accountName: string): Observable<FsLvtToken> {
    return this._httpClient
      .get<FsLvtToken>('/api/integrations/lvt/auth/token/v2/', { params: { accountName } })
      .pipe(
        catchError(() => this._httpClient.get<FsLvtToken>('/api/integrations/lvt/auth/token/')),
      );
  }
}
